import React, { useRef, useState } from "react";
import { Plus, Pencil, Trash2, Copy, X, Star, Upload, ArrowUp, ArrowDown, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input, Textarea, Select } from "@/components/Field";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

const SCHEMAS = {
  services: {
    title: "admin.services", label: (x) => x.name_de,
    fields: [
      ["slug", "text"], ["category", "select", ["web", "mobile", "software"]], ["icon", "text"],
      ["name", "langtext"], ["short", "langarea"], ["desc", "langarea"],
      ["starting_price", "num"], ["delivery_time", "text"], ["technologies", "list"],
      ["features", "langlist"], ["cover_image", "text"], ["featured", "bool"], ["active", "bool"], ["display_order", "num"],
    ],
  },
  packages: {
    title: "admin.packages", label: (x) => x.name_de,
    fields: [
      ["type", "select", ["website", "mobile", "software", "maintenance", "support", "hosting", "custom"]],
      ["name", "langtext"], ["desc", "langarea"],
      ["price", "num"], ["price_discounted", "num"], ["currency", "text"],
      ["billing", "select", ["one_time", "monthly", "annual"]], ["delivery_time", "text"], ["revisions", "num"], ["support_period", "text"],
      ["features", "langlist"], ["excluded", "langlist"], ["cta", "langtext"],
      ["popular", "bool"], ["recommended", "bool"], ["active", "bool"], ["display_order", "num"],
    ],
  },
  portfolio: {
    title: "admin.portfolio", label: (x) => x.title,
    fields: [
      ["title", "text"], ["category", "select", ["web", "mobile", "software"]], ["client", "text"],
      ["short", "langarea"], ["technologies", "list"], ["website_url", "text"],
      ["featured", "bool"], ["active", "bool"], ["display_order", "num"],
    ],
  },
  blog: {
    title: "admin.blog", label: (x) => x.title_de,
    fields: [
      ["title", "langtext"], ["slug", "text"], ["excerpt", "langarea"], ["content", "langarea"],
      ["cover_image", "text"], ["category", "text"], ["author", "text"],
      ["status", "select", ["draft", "published"]], ["tags", "list"],
    ],
  },
  testimonials: {
    title: "admin.testimonials", label: (x) => x.name,
    fields: [
      ["name", "text"], ["role", "langtext"], ["text", "langarea"], ["rating", "num"],
      ["image", "text"], ["active", "bool"], ["display_order", "num"],
    ],
  },
  faqs: {
    title: "admin.faqs", label: (x) => x.q_de,
    fields: [["q", "langtext"], ["a", "langarea"], ["active", "bool"], ["display_order", "num"]],
  },
};

export default function AdminResource({ resource }) {
  const { t } = useI18n();
  const schema = SCHEMAS[resource];
  const { items, loading, reload: load } = useAdminData(`/${resource}`);
  const [editing, setEditing] = useState(null);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/${resource}/${form.id}`, form);
      else await api.post(`/${resource}`, form);
      toast.success("OK"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const persist = async (form) => {
    if (form.id) { await api.put(`/${resource}/${form.id}`, form); return form.id; }
    const r = await api.post(`/${resource}`, form); return r.data.id;
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/${resource}/${id}`); load(); };
  const duplicate = async (id) => { await api.post(`/${resource}/${id}/duplicate`); toast.success("OK"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{t(schema.title)}</h1>
        <button onClick={() => setEditing({})} className="btn-primary !py-2 !px-4 text-sm" data-testid="resource-new"><Plus size={16} /> {t("common.create")}</button>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground">
            <tr><th className="p-4">{t("common.name")}</th><th className="p-4">Active</th><th className="p-4">Order</th><th className="p-4 text-right">{t("common.actions")}</th></tr>
          </thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={4} /> : items.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`row-${it.id}`}>
                <td className="p-4 font-medium flex items-center gap-2">{(it.featured || it.recommended || it.popular) && <Star size={14} className="fill-primary text-primary" />}{schema.label(it) || it.id}</td>
                <td className="p-4">{it.active !== false ? "✓" : "—"}</td>
                <td className="p-4">{it.display_order ?? "—"}</td>
                <td className="p-4">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setEditing(it)} className="p-2 hover:text-primary" data-testid={`edit-${it.id}`}><Pencil size={15} /></button>
                    <button onClick={() => duplicate(it.id)} className="p-2 hover:text-primary" data-testid={`dup-${it.id}`}><Copy size={15} /></button>
                    <button onClick={() => remove(it.id)} className="p-2 hover:text-destructive" data-testid={`del-${it.id}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {editing !== null && <Drawer schema={schema} item={editing} onClose={() => setEditing(null)} onSave={save} persist={persist} reload={load} />}
    </div>
  );
}

function Drawer({ schema, item, onClose, onSave, persist, reload }) {
  const { t, lang } = useI18n();
  const hasImages = schema.fields.some(([, type]) => type === "images");
  const init = {};
  schema.fields.forEach(([key, type]) => {
    if (type === "images") return;
    if (type === "langtext" || type === "langarea") { init[`${key}_de`] = item[`${key}_de`] || ""; init[`${key}_en`] = item[`${key}_en`] || ""; }
    else if (type === "langlist") { init[`${key}_de`] = (item[`${key}_de`] || []).join("\n"); init[`${key}_en`] = (item[`${key}_en`] || []).join("\n"); }
    else if (type === "list") init[key] = (item[key] || []).join("\n");
    else if (type === "bool") init[key] = item[key] ?? false;
    else init[key] = item[key] ?? "";
  });
  const [form, setForm] = useState(init);
  const [pid, setPid] = useState(item.id || null);
  const [media, setMedia] = useState({ cover_image: item.cover_image || "", screenshots: item.screenshots || [] });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildOut = () => {
    const out = { ...item };
    delete out.image_files;
    if (pid) out.id = pid;
    schema.fields.forEach(([key, type]) => {
      if (type === "images") return;
      if (type === "langtext" || type === "langarea") { out[`${key}_de`] = form[`${key}_de`]; out[`${key}_en`] = form[`${key}_en`]; }
      else if (type === "langlist") { out[`${key}_de`] = (form[`${key}_de`] || "").split("\n").filter(Boolean); out[`${key}_en`] = (form[`${key}_en`] || "").split("\n").filter(Boolean); }
      else if (type === "list") out[key] = (form[key] || "").split("\n").filter(Boolean);
      else if (type === "num") out[key] = form[key] === "" ? null : Number(form[key]);
      else out[key] = form[key];
    });
    if (hasImages) { out.cover_image = media.cover_image; out.screenshots = media.screenshots; }
    return out;
  };

  const submit = (e) => {
    e.preventDefault();
    const out = buildOut();
    if ("website_url" in out && out.website_url) {
      try {
        const url = new URL(out.website_url.trim());
        if (url.protocol !== "https:" || url.username || url.password) throw new Error("url");
        out.website_url = url.href;
      } catch {
        toast.error(lang === "de" ? "Bitte eine gültige HTTPS-Website-URL eingeben." : "Please enter a valid HTTPS website URL.");
        return;
      }
    }
    onSave(out);
  };

  const ensureSaved = async () => {
    if (pid) { await persist({ ...buildOut(), id: pid }); return pid; }
    const newId = await persist(buildOut());
    setPid(newId); reload();
    return newId;
  };

  const render = ([key, type, opts]) => {
    const labelDe = key === "website_url" ? (lang === "de" ? "Website-URL (Live-Vorschau)" : "Website URL (live preview)") : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
    if (type === "images") return (
      <ImageManager key={key} pid={pid} ensureSaved={ensureSaved} media={media} setMedia={setMedia} />
    );
    if (type === "bool") return (
      <label key={key} className="flex items-center gap-2.5 text-sm py-1"><input type="checkbox" checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid={`f-${key}`} /> {labelDe}</label>
    );
    if (type === "select") return (
      <Field key={key} label={labelDe}><Select testid={`f-${key}`} value={form[key]} onChange={(e) => set(key, e.target.value)}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</Select></Field>
    );
    if (type === "langtext") return (
      <div key={key} className="grid grid-cols-2 gap-3">
        <Field label={`${labelDe} (DE)`}><Input testid={`f-${key}-de`} value={form[`${key}_de`]} onChange={(e) => set(`${key}_de`, e.target.value)} /></Field>
        <Field label={`${labelDe} (EN)`}><Input testid={`f-${key}-en`} value={form[`${key}_en`]} onChange={(e) => set(`${key}_en`, e.target.value)} /></Field>
      </div>
    );
    if (type === "langarea") return (
      <div key={key} className="grid grid-cols-2 gap-3">
        <Field label={`${labelDe} (DE)`}><Textarea testid={`f-${key}-de`} value={form[`${key}_de`]} onChange={(e) => set(`${key}_de`, e.target.value)} /></Field>
        <Field label={`${labelDe} (EN)`}><Textarea testid={`f-${key}-en`} value={form[`${key}_en`]} onChange={(e) => set(`${key}_en`, e.target.value)} /></Field>
      </div>
    );
    if (type === "langlist") return (
      <div key={key} className="grid grid-cols-2 gap-3">
        <Field label={`${labelDe} DE (1/Zeile)`}><Textarea testid={`f-${key}-de`} value={form[`${key}_de`]} onChange={(e) => set(`${key}_de`, e.target.value)} /></Field>
        <Field label={`${labelDe} EN (1/line)`}><Textarea testid={`f-${key}-en`} value={form[`${key}_en`]} onChange={(e) => set(`${key}_en`, e.target.value)} /></Field>
      </div>
    );
    if (type === "list") return (
      <Field key={key} label={`${labelDe} (1/Zeile)`}><Textarea testid={`f-${key}`} value={form[key]} onChange={(e) => set(key, e.target.value)} /></Field>
    );
    return (
      <Field key={key} label={labelDe}><Input testid={`f-${key}`} type={type === "num" ? "number" : "text"} value={form[key]} onChange={(e) => set(key, e.target.value)} /></Field>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4" data-testid="resource-drawer">
        <div className="flex items-center justify-between sticky top-0 bg-background pb-3 -mt-1">
          <h2 className="font-display text-xl font-black">{item.id ? t("common.edit") : t("common.create")}</h2>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        {schema.fields.map(render)}
        <div className="flex gap-2 pt-2 sticky bottom-0 bg-background pb-1">
          <button className="btn-primary flex-1" data-testid="drawer-save">{t("common.save")}</button>
          <button type="button" onClick={onClose} className="btn-ghost">{t("common.cancel")}</button>
        </div>
      </form>
    </div>
  );
}


function ImageManager({ pid, ensureSaved, media, setMedia }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const shots = media.screenshots || [];

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const id = await ensureSaved();
      for (const file of Array.from(files)) {
        const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
        const { data } = await api.post(`/portfolio/${id}/images`, { data: dataUrl, mime: file.type });
        setMedia((m) => ({ ...m, screenshots: data.screenshots, cover_image: data.cover_image }));
      }
      toast.success(de ? "Bilder hochgeladen" : "Images uploaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImg = async (url) => {
    try {
      const id = await ensureSaved();
      const { data } = await api.delete(`/portfolio/${id}/images`, { data: { url } });
      setMedia((m) => ({ ...m, screenshots: data.screenshots, cover_image: data.cover_image }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setCover = async (url) => {
    try {
      const id = await ensureSaved();
      const { data } = await api.put(`/portfolio/${id}/media`, { cover_image: url });
      setMedia((m) => ({ ...m, cover_image: data.cover_image }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const move = async (i, d) => {
    const j = i + d;
    if (j < 0 || j >= shots.length) return;
    const arr = [...shots];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setMedia((m) => ({ ...m, screenshots: arr }));
    try {
      const id = await ensureSaved();
      await api.put(`/portfolio/${id}/media`, { screenshots: arr });
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-3 border border-border rounded-lg p-3" data-testid="pf-image-manager">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{de ? "Projektbilder & Galerie" : "Project images & gallery"}</span>
        <span className="text-xs text-muted-foreground">{shots.length} {de ? "Bilder" : "images"}</span>
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" data-testid="pf-image-input" onChange={(e) => uploadFiles(e.target.files)} />
      <button type="button" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} className="btn-ghost w-full !py-2 text-sm" data-testid="pf-image-upload">
        <Upload size={15} /> {busy ? (de ? "Lädt hoch..." : "Uploading...") : (de ? "Bilder hochladen (PNG, JPG, WEBP)" : "Upload images (PNG, JPG, WEBP)")}
      </button>

      {shots.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {shots.map((url, i) => (
            <div key={url} className={`relative rounded-md overflow-hidden border-2 ${media.cover_image === url ? "border-primary" : "border-border"}`} data-testid={`pf-image-${i}`}>
              <img src={url} alt="" className="h-20 w-full object-cover" />
              {media.cover_image === url && <span className="absolute top-1 left-1 rounded-full bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 flex items-center gap-0.5"><Star size={9} className="fill-current" /> Cover</span>}
              <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1">
                <div className="flex gap-0.5">
                  <button type="button" onClick={() => move(i, -1)} className="h-6 w-6 rounded bg-black/60 text-white flex items-center justify-center hover:bg-primary" title="←" data-testid={`pf-image-left-${i}`}><ArrowUp size={12} className="-rotate-90" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="h-6 w-6 rounded bg-black/60 text-white flex items-center justify-center hover:bg-primary" title="→" data-testid={`pf-image-right-${i}`}><ArrowDown size={12} className="-rotate-90" /></button>
                </div>
                <div className="flex gap-0.5">
                  {media.cover_image !== url && <button type="button" onClick={() => setCover(url)} className="h-6 w-6 rounded bg-black/60 text-white flex items-center justify-center hover:bg-primary" title="Cover" data-testid={`pf-image-cover-${i}`}><Star size={12} /></button>}
                  <button type="button" onClick={() => removeImg(url)} className="h-6 w-6 rounded bg-black/60 text-white flex items-center justify-center hover:bg-destructive" title="Delete" data-testid={`pf-image-del-${i}`}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <label className="text-xs text-muted-foreground">{de ? "Externe Cover-URL (optional, Fallback)" : "External cover URL (optional fallback)"}</label>
        <Input testid="pf-external-cover" placeholder="https://..." value={media.cover_image || ""} onChange={(e) => setMedia((m) => ({ ...m, cover_image: e.target.value }))} />
        <p className="text-[11px] text-muted-foreground mt-1">{de ? "Neue Projekte werden beim ersten Upload automatisch gespeichert. Bild-Änderungen werden sofort gespeichert." : "New projects are auto-saved on first upload. Image changes save instantly."}</p>
      </div>
    </div>
  );
}
